import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import "../styles/RightsideBox.css";
import BackButton from "./Backbutton";
import { getData } from "../../api/API";
import { getFeaturePermissions } from "../../utils/getpermissions";
import {
  FaPlus,
  FaSync,
  FaList,
  FaChartLine,
  FaFilter,
  FaChevronRight,
  FaBuilding,
  FaBox,
  FaUserTie,
  FaUser,
  FaUserShield,
  FaFileInvoice,
  FaReceipt,
  FaHandshake,
  FaBoxes,
  FaShoppingCart,
  FaSitemap,
  FaSave,
} from "react-icons/fa";

const RightBox = (props) => {
  const location = useLocation();
  const [permissions, setPermissions] = useState({ read: true, write: true });
  const [featureMap, setFeatureMap] = useState({});

  const excludedRoutes = [
    "/company",
    "/employees",
    "/products",
    "/users",
    "/roles",
    "/invoice",
    "/payment",
    "/retailers",
    "/expenses",
    "/customers",
    "/dailysalesrecords",
    "/items",
    "/marketplaceorders",
    "/productmappings",
  ];

  useEffect(() => {
    fetchFeatureMap();
  }, []);

  const fetchFeatureMap = async () => {
    try {
      const res = await getData("features");
      const dynamicMap = {};
      res?.data?.forEach((feature) => {
        const routePath = `/${feature.feature_name
          .toLowerCase()
          .replace(/\s+/g, "")}`;
        dynamicMap[routePath] = feature.feature_name;
      });
      setFeatureMap(dynamicMap);
    } catch (error) {
      console.error("Failed to fetch feature map:", error);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    const feature = featureMap[path];
    if (feature) {
      const perms = getFeaturePermissions(feature);
      if (perms) {
        setPermissions({
          read: perms.read !== undefined ? perms.read : true,
          write: perms.write !== undefined ? perms.write : true,
        });
      } else {
        setPermissions({ read: true, write: true });
      }
    } else {
      setPermissions({ read: true, write: true });
    }
  }, [location.pathname, featureMap]);

  const buttonConfigs = {
    "/company": {
      title: "Company Profile",
      category: "Organization",
      module: "Company",
      icon: <FaBuilding />,
      buttons: [
        { text: "Add Company", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update Info", component: "update", type: "write", icon: <FaSync /> },
        { text: "View Details", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/products": {
      title: "Product Catalog",
      category: "Catalog",
      module: "Products",
      icon: <FaBox />,
      buttons: [
        { text: "Add Product", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/employees": {
      title: "Employee Directory",
      category: "Organization",
      module: "Employees",
      icon: <FaUserTie />,
      buttons: [
        { text: "Add Employee", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/users": {
      title: "User Accounts",
      category: "Administration",
      module: "Users",
      icon: <FaUser />,
      buttons: [
        { text: "Add User", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/roles": {
      title: "Roles & Permissions",
      category: "Administration",
      module: "Roles",
      icon: <FaUserShield />,
      buttons: [
        { text: "Add Role", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/invoice": {
      title: "Invoice Management",
      category: "Sales & Billing",
      module: "Invoices",
      icon: <FaFileInvoice />,
      buttons: [
        { text: "Generate Invoice", component: "generate", type: "write", icon: <FaPlus /> },
        { text: "Recent Invoices", component: "recent", type: "read", icon: <FaList /> },
        { text: "View All", component: "view", type: "read", icon: <FaList /> },
      ],
    },
    "/expenses": {
      title: "Expenses Hub",
      category: "Finance",
      module: "Expenses",
      icon: <FaReceipt />,
      buttons: [
        { text: "Add Expense", component: "add", type: "write", icon: <FaPlus /> },
        { text: "View All Expenses", component: "getAll", type: "read", icon: <FaList /> },
      ],
    },
    "/retailers": {
      title: "Retailer Accounts",
      category: "Sales & Billing",
      module: "Retailers",
      icon: <FaHandshake />,
      buttons: [
        { text: "Add Retailer", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
        { text: "Insights", component: "search", type: "read", icon: <FaChartLine /> },
        { text: "Product List", component: "RetailerProduct", type: "read", icon: <FaFilter /> },
      ],
    },
    "/customers": {
      title: "Customers Directory",
      category: "Sales & Billing",
      module: "Customers",
      icon: <FaUser />,
      buttons: [
        { text: "Add Customer", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/dailysalesrecords": {
      title: "Daily Sales Records",
      category: "Inventory & Billing",
      module: "Sales Log",
      icon: <FaSave />,
      buttons: [
        { text: "Add Record", component: "add", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/payment": {
      title: "Payments Transaction",
      category: "Sales & Billing",
      module: "Payments",
      icon: <FaReceipt />,
      buttons: [
        { text: "Record Payment", component: "searchInvoiceId", type: "write", icon: <FaPlus /> },
        { text: "Update", component: "update", type: "write", icon: <FaSync /> },
        { text: "View All", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/items": {
      title: "Item Inventory",
      category: "Catalog",
      module: "Items",
      icon: <FaBoxes />,
      buttons: [
        { text: "Add Item", component: "add", type: "write", icon: <FaPlus /> },
        { text: "View All Items", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/marketplaceorders": {
      title: "Marketplace Orders",
      category: "Operations",
      module: "Orders",
      icon: <FaShoppingCart />,
      buttons: [
        { text: "Add Order", component: "add", type: "write", icon: <FaPlus /> },
        { text: "View All Orders", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
    "/productmappings": {
      title: "Product Mappings",
      category: "Catalog",
      module: "Mappings",
      icon: <FaSitemap />,
      buttons: [
        { text: "Add Mapping", component: "add", type: "write", icon: <FaPlus /> },
        { text: "View All Mappings", component: "getall", type: "read", icon: <FaList /> },
      ],
    },
  };

  const handleButtonClick = (button) => {
    if (location.pathname === "/invoice") {
      localStorage.removeItem("retailerId");
    }

    if (button.component) {
      props.employeeMethod(button.component);
    } else if (button.onClick) {
      button.onClick();
    }
  };

  const currentPath = location.pathname;
  const config = buttonConfigs[currentPath];

  if (!config) return null;

  const skipPermissionCheck = excludedRoutes.includes(currentPath);

  return (
    <div className="erp-page-toolbar">
      <div className="erp-toolbar-left">
        <BackButton />
        <div className="erp-breadcrumb-container">
          <div className="erp-breadcrumb">
            <span className="erp-breadcrumb-item">Home</span>
            <FaChevronRight className="erp-breadcrumb-separator" />
            <span className="erp-breadcrumb-item">{config.category}</span>
            <FaChevronRight className="erp-breadcrumb-separator" />
            <span className="erp-breadcrumb-active">{config.module}</span>
          </div>

          <div className="erp-page-title-row">
            <div className="erp-page-icon-badge">{config.icon}</div>
            <h2 className="erp-page-title">{config.title}</h2>
          </div>
        </div>
      </div>

      <div className="erp-toolbar-actions">
        {config.buttons.map((button, index) => {
          const isAllowed =
            skipPermissionCheck ||
            (button.type === "read" && permissions.read) ||
            (button.type === "write" && permissions.write);

          return (
            <Button
              key={index}
              variant={button.type === "write" ? "primary" : "light"}
              className={`btn-custom ${button.type !== "write" ? "btn-light" : "btn-primary"}`}
              onClick={() => isAllowed && handleButtonClick(button)}
            >
              {button.icon}
              <span>{button.text}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default RightBox;
